import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, formatPrice } from "@/lib/api";
import { STATUS_LABEL } from "@/context/CustomerContext";

const PayPalPay = ({ order, token, clientId, onPaid }) => (
  <PayPalScriptProvider options={{ clientId, currency: "EUR", intent: "capture", locale: "it_IT", components: "buttons", enableFunding: "card" }}>
    <div data-testid="paypal-buttons" className="mt-5 max-w-md">
      <PayPalButtons
        style={{ layout: "vertical", color: "black", shape: "rect", label: "pay" }}
        createOrder={async (data) => (await api.post(`/orders/${order.number}/paypal/create`, null, { params: { t: token, source: data?.paymentSource || "paypal" } })).data.id}
        onApprove={async () => {
          try { onPaid((await api.post(`/orders/${order.number}/paypal/capture`, null, { params: { t: token } })).data); toast.success("Pagamento completato"); } catch (e) { toast.error(apiError(e)); }
        }}
        onError={() => toast.error("Errore durante il pagamento PayPal. Puoi riprovare.")}
      />
    </div>
  </PayPalScriptProvider>
);

const BankInfo = ({ order }) => {
  const b = order.payment.bank || {};
  return (
    <div className="mt-6 p-5 stitch bg-[#E9DFCE]/40 text-sm space-y-1" data-testid="bank-instructions">
      <div className="font-serif text-xl text-[#39251D] mb-2">Istruzioni per il bonifico</div>
      <div>Intestatario: <b>{b.holder}</b></div>
      <div>IBAN: <b className="tracking-wider" data-testid="bank-iban">{b.iban}</b></div>
      {b.bank_name && <div>Banca: {b.bank_name}</div>}
      <div>Importo: <b>{formatPrice(order.total)}</b></div>
      <div>Causale: <b>Ordine {order.number}</b></div>
      <div>Entro 24 ore: <b data-testid="bank-due">{new Date(order.payment.due_at).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</b></div>
      <p className="pt-2 text-xs text-[#242321]/70">Prepareremo e spediremo l'ordine dopo la conferma dell'accredito. Se il pagamento non arriva entro 24 ore, l'ordine verrà annullato dopo una nostra verifica.</p>
    </div>
  );
};

export default function OrderPage() {
  const { number } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t");
  const [order, setOrder] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get(`/orders/${number}`, { params: { t: token } }).then((r) => setOrder(r.data)).catch(() => setError(true));
    api.get("/shop/config").then((r) => setCfg(r.data));
  }, [number, token]);

  if (error) return <p className="py-32 text-center font-serif text-2xl" data-testid="order-not-found">Ordine non trovato.</p>;
  if (!order) return <div className="max-w-3xl mx-auto px-5 pt-16"><div className="h-40 bg-[#E9DFCE] animate-pulse" /></div>;
  const pending = order.status === "in_attesa_pagamento";
  const a = order.shipping_address;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-14" data-testid="order-page">
      <div className="flex items-center gap-3 text-[#956842]">{pending ? <Clock className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6 text-[#3d5a3a]" />}<span className="text-[11px] uppercase tracking-[0.25em]" data-testid="order-status">{STATUS_LABEL[order.status]}</span></div>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#39251D] mt-3">Grazie, {order.customer.first_name}</h1>
      <p className="mt-3 text-sm text-[#242321]/75">Ordine <b data-testid="order-number">{order.number}</b> · riceverai il riepilogo all'indirizzo {order.customer.email}.</p>
      {pending && order.payment.method === "paypal" && cfg?.paypal_client_id && (
        <div className="mt-8"><div className="font-serif text-xl text-[#39251D]">Completa il pagamento con PayPal</div>
          <p className="text-xs text-[#242321]/60">Il pezzo resta riservato per 60 minuti.{cfg.paypal_mode === "sandbox" ? " Modalità di prova: usa un conto sandbox PayPal." : ""}</p>
          <PayPalPay order={order} token={token} clientId={cfg.paypal_client_id} onPaid={setOrder} /></div>
      )}
      {pending && order.payment.method === "bonifico" && <BankInfo order={order} />}
      <ul className="mt-10 divide-y divide-[#D5B98C]/40 border-y border-[#D5B98C]/40">
        {order.items.map((i) => (
          <li key={i.product_id + (i.variant || "")} className="flex gap-4 py-4 text-sm">
            {i.image && <img src={i.image} alt="" className="w-14 h-16 object-cover" />}
            <div className="flex-1"><div className="font-serif text-lg text-[#39251D]">{i.name}</div><div className="text-xs text-[#242321]/60">{i.sku}{i.variant ? ` · ${i.variant}` : ""} · Qtà {i.qty}</div></div>
            <span>{formatPrice(i.price * i.qty)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 space-y-1 text-sm max-w-xs ml-auto">
        <div className="flex justify-between"><span>Subtotale</span><span>{formatPrice(order.subtotal)}</span></div>
        <div className="flex justify-between"><span>Spedizione (BRT)</span><span>{order.shipping === 0 ? "Gratuita" : formatPrice(order.shipping)}</span></div>
        <div className="flex justify-between text-lg pt-2 border-t border-[#D5B98C]/50"><b>Totale</b><b data-testid="order-total">{formatPrice(order.total)}</b></div>
      </div>
      <div className="mt-8 text-sm"><div className="text-[11px] uppercase tracking-[0.2em] text-[#956842] mb-1">Spedizione a</div>{a.full_name}<br />{a.street}<br />{a.zip} {a.city} {a.province}<br />{a.country_name}</div>
      {order.tracking?.code && <p className="mt-4 text-sm" data-testid="order-tracking">Tracciamento BRT: <b>{order.tracking.code}</b></p>}
    </div>
  );
}
